/**
 * Generic helpers for a Pod feature that needs to escape its iframe into a
 * real top-level popup window.
 *
 * When the Pod is embedded in a PSA (Halo, ConnectWise, Autotask) iframe, the
 * parent page rarely sets `allow="microphone"` (or other sensitive
 * permissions-policy features) on its iframe element. In that case a
 * permission-gated API rejects - often *without ever prompting* the user -
 * and there is no JS workaround at the iframed page level.
 *
 * The escape hatch is to open the same Pod URL in a real browser window via
 * `window.open(...)`. The popup is NOT iframed, so the browser applies no
 * inherited permissions-policy restriction and the normal permission prompt
 * fires.
 *
 * This is the feature-agnostic half of the pattern CallScribe's softphone
 * popup established (components/CallScribe/popout.ts +
 * components/CallScribe/popupChannel.ts) - extracted so a second feature
 * (Alpha voice) can reuse it without importing from a CallScribe-named path.
 * CallScribe itself is NOT migrated onto this module in this change; that
 * dedupe is a follow-up once both are on staging.
 */

/** Returns true when the current document is rendered inside another window/frame. */
export const isEmbeddedInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin parent throws on `window.top` access — that itself proves we're iframed.
    return true;
  }
};

export interface IOpenPopoutParams {
  url: string;
  name: string;
  features?: string;
}

const DEFAULT_FEATURES =
  'popup=yes,width=520,height=760,menubar=no,toolbar=no,location=no,status=no,resizable=yes';

const childWindows = new Set<Window>();

/** Opens (or focuses, if a window by this `name` is already open) a popup at
 * `url`. Returns the popup window, or `null` if the browser blocked it (a
 * popup blocker — happens when not invoked from a user gesture). */
export const openPopoutWindow = ({
  url,
  name,
  features = DEFAULT_FEATURES,
}: IOpenPopoutParams): Window | null => {
  const popup = window.open(url, name, features);
  if (popup) {
    childWindows.add(popup);
    popup.focus();
  }
  return popup;
};

/** Returns the window that opened the current document, or null (not a popup,
 * or the opener reference was severed). */
export const openerWindow = (): Window | null => {
  try {
    return (window.opener as Window | null) ?? null;
  } catch {
    return null;
  }
};

export const hasOpener = (): boolean => openerWindow() !== null;

const READY = 'mspp.popout.ready';
const READY_TIMEOUT_MS = 30_000;

interface IReadyMessage {
  type: typeof READY;
  to: string;
}

export interface IReadyHandoffSender<T> {
  /** Sends `payload` to the popup once it signals ready for `to`, or
   * immediately if it already has. Only the LATEST queued payload is kept -
   * this is a handoff, not a message queue. */
  send: (payload: T) => void;
  dispose: () => void;
}

/**
 * Opener-side half of a one-shot, ready-gated handoff to a popup opened via
 * {@link openPopoutWindow}: the browser's own popup gesture (window.open)
 * races with the popup's React app mounting, so the opener can't just
 * postMessage right away — it queues until the popup posts a READY message
 * tagged with the same `to`, origin-checked, then flushes and tears down.
 */
export const createReadyHandoffSender = <T extends Record<string, unknown>>(
  popup: Window,
  to: string,
): IReadyHandoffSender<T> => {
  let ready = false;
  let queued: T | null = null;
  let expiry = 0;

  const post = (message: T): void => {
    try {
      popup.postMessage(message, window.location.origin);
    } catch {
      // popup gone / cross-origin — nothing to do
    }
  };

  const dispose = (): void => {
    window.removeEventListener('message', onMessage);
    window.clearTimeout(expiry);
  };

  const flush = (): void => {
    if (!ready || queued === null) return;
    post(queued);
    queued = null;
    dispose();
  };

  function onMessage(event: MessageEvent): void {
    if (event.origin !== window.location.origin) return;
    if (event.source !== popup) return;
    const data = event.data as Partial<IReadyMessage> | null;
    if (!data || data.type !== READY || data.to !== to) return;
    ready = true;
    flush();
  }

  window.addEventListener('message', onMessage);
  expiry = window.setTimeout(dispose, READY_TIMEOUT_MS);

  return {
    send: (payload: T) => {
      queued = payload;
      flush();
    },
    dispose,
  };
};

/**
 * Popup-side half: announces READY to the opener (tagged `to`) and invokes
 * `onMessage` for whatever the opener's {@link createReadyHandoffSender}
 * later sends, origin-checked. Returns an unsubscribe function. A no-op if
 * there is no opener (the popup was opened directly, not via handoff).
 */
export const announceReadyAndListen = <T>(
  to: string,
  onMessage: (data: T) => void,
): (() => void) => {
  const opener = openerWindow();
  if (!opener || !to) return () => undefined;

  const handler = (event: MessageEvent): void => {
    if (event.origin !== window.location.origin) return;
    if (event.source !== opener) return;
    const data = event.data as (T & { type?: string }) | null;
    if (!data) return;
    onMessage(data);
  };

  window.addEventListener('message', handler);

  try {
    const ready: IReadyMessage = { type: READY, to };
    opener.postMessage(ready, window.location.origin);
  } catch {
    window.removeEventListener('message', handler);
    return () => undefined;
  }

  return () => window.removeEventListener('message', handler);
};

const isKnownChild = (source: MessageEventSource | null): boolean => {
  if (childWindows.size === 0) return true;
  for (const child of childWindows) {
    if (child === source) return true;
  }
  return false;
};

/** Popup -> opener, fire-and-forget, for status updates AFTER the ready
 * handoff (e.g. "the popup ended its session") - not part of the handoff
 * itself, so there's no queueing or ready-gating here. */
export const postToOpener = (
  message: Record<string, unknown> & { type: string },
): void => {
  const opener = openerWindow();
  if (!opener) return;
  try {
    opener.postMessage(message, window.location.origin);
  } catch {
    // popup gone / cross-origin — nothing to do
  }
};

/** Opener-side listener for {@link postToOpener} messages from a window
 * opened via {@link openPopoutWindow} (or any registered child). */
export const listenForChildMessage = (
  type: string,
  onMessage: (data: Record<string, unknown>) => void,
): (() => void) => {
  const handler = (event: MessageEvent): void => {
    if (event.origin !== window.location.origin) return;
    if (!isKnownChild(event.source)) return;
    const data = event.data as Record<string, unknown> | null;
    if (!data || data.type !== type) return;
    onMessage(data);
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
};
